'use client';

import { entityUrl } from '@/lib/entity-url';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Search, X, Tag as TagIcon, Twitter } from 'lucide-react';
import { EntityType, Tag } from '@/types/tags';
import { ENTITY_CONFIG } from '@/lib/tags-config';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

// Match context type - shows WHY/WHERE a result matched
type MatchContext = {
    field: 'name' | 'description' | 'entityType' | 'twitterHandle' | 'slug';
    matchedText: string;
    fullText: string;
};

interface SearchResult {
    tag: Tag;
    matchContext: MatchContext[];
    score: number;
}

/**
 * Visual skins.
 *  - 'default'   — the original gray/blue Tailwind skin (Navbar, GlobalSearchModal,
 *                  social page). UNCHANGED so existing call sites keep their look.
 *  - 'dashboard' — token-native Descout dashboard chrome (border-divider / bg-surface /
 *                  gold accents / mono labels), consistent with Directory.tsx's
 *                  inline-filter styling. Used by the shell's ⌘K palette.
 */
export type DirectorySearchSkin = 'default' | 'dashboard';

interface DirectorySearchProps {
    tags: Tag[];
    onTagSelect?: (tag: Tag) => void;
    onFilterChange?: (filteredTags: Tag[]) => void;
    selectedTagId?: string;
    mode?: 'navigate' | 'filter'; // navigate = go to page, filter = filter in-place
    placeholder?: string;
    className?: string;
    /** Custom navigation handler - return URL string for custom routing, or void to use default /tags/{slug} */
    getNavigationUrl?: (tag: Tag) => string;
    /** Visual skin (see DirectorySearchSkin). Default: the original gray/blue skin. */
    skin?: DirectorySearchSkin;
    /**
     * Own the global ⌘K/Ctrl+K focus shortcut (+ Esc-to-clear) and show the kbd hint.
     * Default TRUE (existing behavior). Pass FALSE when a parent (e.g. the dashboard
     * shell) already owns ⌘K — one shortcut owner per page.
     */
    hotkey?: boolean;
    /** Focus the input on mount (modal/palette usage). */
    autoFocus?: boolean;
    /**
     * Navigate mode: when focused with an EMPTY query, show the first N tags as
     * browse suggestions (the old shell-palette behavior). 0 (default) = off.
     */
    idleResults?: number;
    /**
     * Render results in normal document flow (a stacked panel below the input)
     * instead of an absolutely-positioned floating dropdown. For command-palette
     * hosts whose panel clips overflow / has a footer below the search.
     */
    inlineResults?: boolean;
    /**
     * Intercept selection: prevents link navigation and calls this instead
     * (e.g. open the in-shell profile drawer). Result hrefs stay real crawlable
     * links, so middle-click / open-in-new-tab still work.
     */
    onTagActivate?: (tag: Tag) => void;
}

// Highlight matched text in a string
function HighlightMatch({ text, query, markClass }: { text: string; query: string; markClass: string }) {
    if (!query.trim()) return <>{text}</>;

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return (
        <>
            {parts.map((part, i) =>
                regex.test(part) ? (
                    <mark key={i} className={markClass}>
                        {part}
                    </mark>
                ) : (
                    <span key={i}>{part}</span>
                )
            )}
        </>
    );
}

/* ── Skin class maps ─────────────────────────────────────────────────────── */

const SKINS = {
    default: {
        searchIconWrap: 'absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none',
        searchIcon: 'h-5 w-5 text-gray-400',
        input: 'block w-full pl-12 pr-24 py-3 text-base border-2 border-gray-200 dark:border-neutral-700 rounded-xl bg-white dark:bg-neutral-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-400 transition-all',
        clearBtn: 'p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors',
        kbdWrap: 'hidden sm:flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-neutral-800 text-xs text-gray-500 dark:text-gray-400 font-medium',
        dropdownFloating: 'absolute top-full left-0 right-0 mt-2 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-xl shadow-2xl z-50 max-h-[480px] overflow-y-auto',
        dropdownInline: 'mt-2 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-xl max-h-[46vh] overflow-y-auto',
        countBar: 'px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-neutral-800 flex items-center justify-between',
        countStrong: 'font-semibold text-gray-900 dark:text-white',
        countHint: 'text-gray-400',
        row: 'block px-4 py-3 transition-colors border-b border-gray-50 dark:border-neutral-800/50 last:border-b-0',
        rowIdle: 'hover:bg-gray-50 dark:hover:bg-neutral-800',
        rowActive: 'bg-gray-50 dark:bg-neutral-800',
        imgWrap: 'relative w-12 h-12 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100 dark:bg-neutral-800',
        imgPlaceholder: 'w-12 h-12 flex-shrink-0 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-neutral-800 dark:to-neutral-700 flex items-center justify-center',
        imgPlaceholderIcon: 'w-5 h-5 text-gray-400',
        name: 'font-semibold text-gray-900 dark:text-white',
        mark: 'bg-yellow-200 dark:bg-yellow-500/30 text-inherit px-0.5 rounded',
        typeBadge: 'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-gray-400',
        twitterIcon: 'text-sky-500',
        matchBadgeBase: 'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md',
        articleCount: 'mt-1 text-xs text-gray-500 dark:text-gray-400',
        overflowBar: 'px-4 py-3 text-center text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-800/50',
        noResults: 'px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400',
        filterCount: 'mt-2 text-sm text-gray-600 dark:text-gray-400 text-center',
        filterCountStrong: 'font-semibold text-gray-900 dark:text-white',
    },
    dashboard: {
        searchIconWrap: 'absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none',
        searchIcon: 'h-4 w-4 text-parchment-dim',
        input: 'block w-full pl-9 pr-20 py-2.5 rounded border-[0.5px] border-divider bg-ink/40 font-display text-sm text-parchment placeholder:text-parchment-dim focus:outline-none focus:border-scout-gold/40 transition-colors',
        clearBtn: 'p-1 rounded hover:bg-parchment-faint text-parchment-dim hover:text-parchment transition-colors',
        kbdWrap: 'hidden sm:flex items-center gap-1 px-1.5 py-0.5 rounded border-[0.5px] border-divider font-mono text-[10px] text-parchment-dim',
        dropdownFloating: 'absolute top-full left-0 right-0 mt-2 bg-surface border-[0.5px] border-divider rounded shadow-2xl z-50 max-h-[480px] overflow-y-auto',
        dropdownInline: 'mt-1.5 max-h-[46vh] overflow-y-auto',
        countBar: 'px-3 py-2 font-mono text-[10px] uppercase tracking-[0.08em] text-parchment-dim border-b border-divider flex items-center justify-between',
        countStrong: 'font-semibold text-parchment',
        countHint: 'text-parchment-dim',
        row: 'block px-3 py-2.5 transition-colors rounded',
        rowIdle: 'hover:bg-parchment-faint',
        rowActive: 'bg-parchment-faint',
        imgWrap: 'relative w-9 h-9 flex-shrink-0 rounded overflow-hidden bg-ink',
        imgPlaceholder: 'w-9 h-9 flex-shrink-0 rounded bg-ink flex items-center justify-center',
        imgPlaceholderIcon: 'w-4 h-4 text-parchment-dim',
        name: 'font-display font-semibold text-[13.5px] text-parchment',
        mark: 'bg-scout-gold/25 text-inherit px-0.5 rounded-sm',
        typeBadge: 'inline-flex items-center gap-1 font-mono text-[9.5px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded border-[0.5px] border-divider text-parchment-dim',
        twitterIcon: 'text-parchment-dim',
        matchBadgeBase: 'inline-flex items-center gap-1 font-mono text-[9.5px] uppercase tracking-[0.06em] px-1.5 py-0.5 rounded border-[0.5px]',
        articleCount: 'mt-1 font-mono text-[10px] text-parchment-dim',
        overflowBar: 'px-3 py-2.5 text-center font-mono text-[10px] uppercase tracking-[0.08em] text-parchment-dim border-t border-divider',
        noResults: 'px-3 py-6 text-center font-mono text-xs text-parchment-dim',
        filterCount: 'mt-2 font-mono text-xs text-parchment-dim text-center',
        filterCountStrong: 'font-semibold text-parchment',
    },
} as const;

// Match-context badge color, per skin.
function matchBadgeStyle(skin: DirectorySearchSkin, field: MatchContext['field']): string {
    if (skin === 'dashboard') {
        return field === 'name'
            ? 'border-scout-gold/40 bg-scout-gold/10 text-scout-gold'
            : 'border-divider bg-ink/40 text-parchment-dim';
    }
    switch (field) {
        case 'name':
            return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
        case 'description':
            return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
        case 'entityType':
            return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
        case 'twitterHandle':
            return 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400';
        default:
            return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    }
}

export default function DirectorySearch({
    tags,
    onTagSelect,
    onFilterChange,
    selectedTagId,
    mode = 'navigate',
    placeholder = "Search entities by name, description, or type...",
    className = '',
    getNavigationUrl,
    skin = 'default',
    hotkey = true,
    autoFocus = false,
    idleResults = 0,
    inlineResults = false,
    onTagActivate,
}: DirectorySearchProps) {
    const [inputValue, setInputValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [isMac, setIsMac] = useState(true); // Default to Mac symbol, detect on mount
    const [activeIdx, setActiveIdx] = useState(-1); // keyboard-highlighted result

    const searchContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const router = useRouter();
    const S = SKINS[skin];

    // Detect platform on mount
    useEffect(() => {
        setIsMac(navigator.platform.toUpperCase().indexOf('MAC') >= 0);
    }, []);

    // Autofocus (palette/modal hosts).
    useEffect(() => {
        if (!autoFocus) return;
        const t = setTimeout(() => inputRef.current?.focus(), 20);
        return () => clearTimeout(t);
    }, [autoFocus]);

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(inputValue);
        }, 150);

        return () => clearTimeout(timer);
    }, [inputValue]);

    // Search and score results with match context
    const searchResults = useMemo((): SearchResult[] => {
        if (!searchQuery.trim()) {
            // In filter mode, return all tags when no query
            if (mode === 'filter') {
                return tags.map(tag => ({ tag, matchContext: [], score: 0 }));
            }
            return [];
        }

        const q = searchQuery.toLowerCase();
        const results: SearchResult[] = [];

        for (const tag of tags) {
            const matchContext: MatchContext[] = [];
            let score = 0;

            // Check name match (highest priority)
            if (tag.name.toLowerCase().includes(q)) {
                matchContext.push({
                    field: 'name',
                    matchedText: tag.name,
                    fullText: tag.name,
                });
                score += tag.name.toLowerCase() === q ? 100 : 50; // Exact match gets higher score
            }

            // Check slug match
            if (tag.slug.toLowerCase().includes(q)) {
                matchContext.push({
                    field: 'slug',
                    matchedText: tag.slug,
                    fullText: tag.slug,
                });
                score += 30;
            }

            // Check description match
            if (tag.description?.toLowerCase().includes(q)) {
                // Extract snippet around match
                const desc = tag.description;
                const matchIndex = desc.toLowerCase().indexOf(q);
                const start = Math.max(0, matchIndex - 30);
                const end = Math.min(desc.length, matchIndex + q.length + 30);
                const snippet = (start > 0 ? '...' : '') + desc.slice(start, end) + (end < desc.length ? '...' : '');

                matchContext.push({
                    field: 'description',
                    matchedText: snippet,
                    fullText: desc,
                });
                score += 20;
            }

            // Check entity type match
            if (tag.entityType?.toLowerCase().includes(q)) {
                matchContext.push({
                    field: 'entityType',
                    matchedText: tag.entityType,
                    fullText: tag.entityType,
                });
                score += 15;
            }

            // Check Twitter handles match
            if (tag.twitterHandles?.some(h => h.toLowerCase().includes(q))) {
                const matchedHandle = tag.twitterHandles.find(h => h.toLowerCase().includes(q));
                if (matchedHandle) {
                    matchContext.push({
                        field: 'twitterHandle',
                        matchedText: `@${matchedHandle}`,
                        fullText: tag.twitterHandles.map(h => `@${h}`).join(', '),
                    });
                    score += 25;
                }
            }

            // Add to results if any match found
            if (matchContext.length > 0) {
                // Boost score based on article count (popularity)
                score += Math.min(tag.articleCount || 0, 10);
                results.push({ tag, matchContext, score });
            }
        }

        // Sort by score (highest first)
        return results.sort((a, b) => b.score - a.score);
    }, [tags, searchQuery, mode]);

    // Notify parent of filter changes in filter mode
    useEffect(() => {
        if (mode === 'filter' && onFilterChange) {
            onFilterChange(searchResults.map(r => r.tag));
        }
    }, [searchResults, mode, onFilterChange]);

    // The rows actually rendered in navigate mode: scored matches when there's a
    // query, otherwise the idle browse list (first N tags) when enabled.
    const visibleResults = useMemo((): SearchResult[] => {
        if (mode !== 'navigate') return [];
        if (searchQuery.trim()) return searchResults.slice(0, 15);
        if (idleResults > 0) {
            return tags.slice(0, idleResults).map(tag => ({ tag, matchContext: [], score: 0 }));
        }
        return [];
    }, [mode, searchQuery, searchResults, tags, idleResults]);

    const hasQuery = searchQuery.trim().length > 0;

    // Show the results panel when focused: matches (or a no-results row) on query,
    // the idle browse list otherwise.
    const showSearchResults =
        mode === 'navigate' &&
        isSearchFocused &&
        (hasQuery ? true : visibleResults.length > 0);

    // Reset keyboard highlight whenever the result set changes / panel closes.
    useEffect(() => {
        setActiveIdx(-1);
    }, [searchQuery, isSearchFocused]);

    // Keep the keyboard-highlighted row scrolled into view.
    useEffect(() => {
        if (activeIdx < 0) return;
        dropdownRef.current
            ?.querySelector(`[data-ridx="${activeIdx}"]`)
            ?.scrollIntoView({ block: 'nearest' });
    }, [activeIdx]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
                setIsSearchFocused(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Keyboard shortcut: Cmd/Ctrl + K (only when this instance owns the hotkey).
    useEffect(() => {
        if (!hotkey) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                inputRef.current?.focus();
            }

            // Escape to clear
            if (e.key === 'Escape' && isSearchFocused) {
                setInputValue('');
                setSearchQuery('');
                inputRef.current?.blur();
                setIsSearchFocused(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hotkey, isSearchFocused]);

    const clearAndClose = useCallback(() => {
        setInputValue('');
        setSearchQuery('');
        setIsSearchFocused(false);
    }, []);

    // Activate a result from the KEYBOARD (no link click to ride on).
    const activateResult = (tag: Tag) => {
        if (onTagActivate) {
            onTagActivate(tag);
        } else {
            router.push(getTagUrl(tag));
        }
        if (onTagSelect) onTagSelect(tag);
        clearAndClose();
    };

    const handleTagClick = (tag: Tag, e?: React.MouseEvent) => {
        // Intercept mode (e.g. open the in-shell profile drawer) — the href stays
        // a real crawlable link for middle-click / new-tab.
        if (onTagActivate) {
            e?.preventDefault();
            onTagActivate(tag);
        } else if (getNavigationUrl) {
            // If custom navigation is provided, use router.push instead of Link
            e?.preventDefault();
            const url = getNavigationUrl(tag);
            router.push(url);
        }

        if (onTagSelect) {
            onTagSelect(tag);
        }
        clearAndClose();
    };

    // Arrow-key + Enter navigation over the visible results.
    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (mode !== 'navigate' || visibleResults.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx(i => Math.min(i + 1, visibleResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx(i => Math.max(i - 1, -1));
        } else if (e.key === 'Enter') {
            const pick = visibleResults[activeIdx >= 0 ? activeIdx : 0];
            if (pick) {
                e.preventDefault();
                activateResult(pick.tag);
            }
        }
    };

    // Get the navigation URL for a tag
    const getTagUrl = (tag: Tag): string => {
        if (getNavigationUrl) {
            return getNavigationUrl(tag);
        }
        return entityUrl(tag.slug, { trailingSlash: false });
    };

    const handleClearSearch = () => {
        clearAndClose();
    };

    const getPrimaryImage = (tag: Tag) => {
        return tag.images?.find(img => img.isPrimary)?.url || tag.images?.[0]?.url;
    };

    const getMatchFieldLabel = (field: MatchContext['field']) => {
        switch (field) {
            case 'name': return 'Name';
            case 'description': return 'Description';
            case 'entityType': return 'Type';
            case 'twitterHandle': return 'Twitter';
            case 'slug': return 'Slug';
        }
    };

    return (
        <div className={className} ref={searchContainerRef}>
            {/* Search Input */}
            <div className="relative">
                <div className={S.searchIconWrap}>
                    <Search className={S.searchIcon} />
                </div>
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onKeyDown={handleInputKeyDown}
                    placeholder={placeholder}
                    className={S.input}
                />

                {/* Right Side Actions */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                    {inputValue && (
                        <button
                            onClick={handleClearSearch}
                            className={S.clearBtn}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}

                    {/* Keyboard Shortcut Hint - Platform aware (only when this instance owns ⌘K) */}
                    {hotkey && (
                        <div className={S.kbdWrap}>
                            <kbd className="font-sans">{isMac ? '⌘' : 'Ctrl'}</kbd>
                            <kbd className="font-sans">K</kbd>
                        </div>
                    )}
                </div>

                {/* Search Results — floating dropdown OR in-flow panel (palette hosts) */}
                {showSearchResults && (
                    <div
                        ref={dropdownRef}
                        className={inlineResults ? S.dropdownInline : S.dropdownFloating}
                    >
                        <div className="py-2">
                            {/* Results count / idle header */}
                            <div className={S.countBar}>
                                <span>
                                    {hasQuery ? (
                                        <>
                                            Found <span className={S.countStrong}>{searchResults.length}</span> {searchResults.length === 1 ? 'result' : 'results'}
                                        </>
                                    ) : (
                                        'Entities'
                                    )}
                                </span>
                                <span className={S.countHint}>↑↓ · Enter to select</span>
                            </div>

                            {visibleResults.length === 0 && hasQuery && (
                                <div className={S.noResults}>
                                    No matches for &ldquo;{searchQuery}&rdquo;
                                </div>
                            )}

                            {visibleResults.map(({ tag, matchContext }, ridx) => (
                                <Link
                                    key={tag.id}
                                    href={getTagUrl(tag)}
                                    data-ridx={ridx}
                                    onClick={(e) => handleTagClick(tag, e)}
                                    className={`${S.row} ${ridx === activeIdx ? S.rowActive : S.rowIdle}`}
                                >
                                    <div className="flex items-start gap-3">
                                        {/* Entity Image */}
                                        {getPrimaryImage(tag) ? (
                                            <div className={S.imgWrap}>
                                                <Image
                                                    src={getPrimaryImage(tag)!}
                                                    alt={tag.name}
                                                    fill
                                                    className="object-cover"
                                                />
                                            </div>
                                        ) : (
                                            <div className={S.imgPlaceholder}>
                                                <TagIcon className={S.imgPlaceholderIcon} />
                                            </div>
                                        )}

                                        {/* Entity Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={S.name}>
                                                    <HighlightMatch text={tag.name} query={searchQuery} markClass={S.mark} />
                                                </span>

                                                {/* Entity Type Badge */}
                                                {tag.entityType && ENTITY_CONFIG[tag.entityType] && (
                                                    <span className={S.typeBadge}>
                                                        {React.createElement(ENTITY_CONFIG[tag.entityType].icon, { className: 'w-3.5 h-3.5' })}
                                                        {ENTITY_CONFIG[tag.entityType].singularLabel}
                                                    </span>
                                                )}

                                                {/* Twitter indicator */}
                                                {tag.twitterHandles && tag.twitterHandles.length > 0 && (
                                                    <span className={S.twitterIcon} title="Has Twitter">
                                                        <Twitter className="w-3.5 h-3.5" />
                                                    </span>
                                                )}
                                            </div>

                                            {/* Match Context - Shows WHERE/WHY this result matched */}
                                            {matchContext.length > 0 && (
                                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                                    {matchContext.slice(0, 2).map((context, idx) => (
                                                        <span
                                                            key={idx}
                                                            className={`${S.matchBadgeBase} ${matchBadgeStyle(skin, context.field)}`}
                                                        >
                                                            <span className="font-medium">{getMatchFieldLabel(context.field)}:</span>
                                                            <span className="truncate max-w-[200px]">
                                                                <HighlightMatch text={context.matchedText} query={searchQuery} markClass={S.mark} />
                                                            </span>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Article count */}
                                            {(tag.articleCount || 0) > 0 && (
                                                <div className={S.articleCount}>
                                                    {tag.articleCount} {tag.articleCount === 1 ? 'article' : 'articles'}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}

                            {hasQuery && searchResults.length > 15 && (
                                <div className={S.overflowBar}>
                                    Showing 15 of {searchResults.length} results. Refine your search for better results.
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Filter mode: show count below search */}
            {mode === 'filter' && searchQuery && (
                <div className={S.filterCount}>
                    {searchResults.length === 0 ? (
                        <span>No entities found matching "{searchQuery}"</span>
                    ) : (
                        <span>
                            Showing <span className={S.filterCountStrong}>{searchResults.length}</span> of {tags.length} entities
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}
